import {
  Button,
  CheckboxInput,
  DateInput,
  FieldSet,
  FormWrapper,
  ProgressTracker,
  ReviewSubmission,
  SignatureInput,
  TextInput,
} from 'components'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  useApi,
  useDynamicFormContent,
  useGlobalState,
  useOneStop,
  useUtilities,
} from 'hooks'
import { BsCheck, BsExclamationTriangleFill } from 'react-icons/bs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useLocalStorageState from 'hooks/use-localstorage-state/use-localstorage-state'
import { useTranslation } from 'react-i18next'
import './form.scss'
import { mailInBallotConfig, voterRegistrationConfig } from './config'
import { PhoneInput } from 'components/PhoneInput/PhoneInput'
import { SelectInput } from 'components/SelectInput/SelectInput'
import {
  ApiFormId,
  BlurEvent,
  Field,
  FieldError,
  ValidateFieldType,
} from 'common.types'
import { statusLookupConfig } from './config/statusLookup.config'
import Accordion from 'components/Accordion/Accordion'
import {
  FORM_KEYS_TO_ADD_TO_GLOBAL_STATE,
  ONE_STOP_FORMS_TO_SEED,
  SUBMISSIONS_NAME,
} from './form.constants'
import { GlobalStoreContext } from 'stores/GlobalStore'
import { toJS } from 'mobx'

type KeyAnyValue = {
  [key: string]: any
}

const FIRST_STEP = 1

const EMPTY_STATE: KeyAnyValue = {
  step: FIRST_STEP,
}

type FormProps = {
  apiName: ApiFormId
}

export const Form: React.FC<FormProps> = ({ apiName }) => {
  const i18nName = useMemo(() => {
    switch (apiName) {
      case 'mail-in-ballot':
        return 'mailInBallotForm'
      case 'voter-registration':
        return 'voterRegistrationForm'
      case 'status-lookup':
        return 'statusLookupForm'
    }
  }, [apiName])
  const globalStore = useContext(GlobalStoreContext)
  const {
    fields: RAW_FIELD_CONFIG,
    requiredFieldGroups,
    validateEventResponse,
    captchaStep,
    requireEitherEmailOrPhone,
    pollingOnSubmit,
    validateEventResponseType,
    validateStep,
    stateChangeHandler,
    afterDateAlert,
    headerContent,
    footerContent,
    aboveFieldsContent: AboveFieldsContent,
    belowFieldsContent: BelowFieldsContent,
    successScreen: SuccessScreen,
    reviewSubmission,
    singlePage,
  } = useMemo(() => {
    switch (apiName) {
      case 'mail-in-ballot':
        return mailInBallotConfig
      case 'voter-registration':
        return voterRegistrationConfig
      case 'status-lookup':
        return statusLookupConfig
    }
  }, [apiName])

  const { t, i18n } = useTranslation(undefined, { keyPrefix: i18nName })
  const { t: globalT } = useTranslation(undefined, { keyPrefix: 'global' })
  const INITIAL_STATE = {
    ...EMPTY_STATE,
  }
  const [state, setState] = useLocalStorageState(apiName, INITIAL_STATE)
  const { formContent, mailinBallotDeadlinePassed } = useDynamicFormContent()
  const [showAfterDateAlert, setShowAfterDateAlert] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [inReview, setInReview] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const { submitForm, triggerFormEvent, recoveredFormState, error, loading } =
    useApi(apiName, pollingOnSubmit)
  const {
    formatDateToMMDDYYYY,
    validateSignature,
    validateDate,
    validateEmail,
    validatePhoneNumber,
    validatePennDotId,
    validateSsnLastFour,
    validateZip,
    randomString,
    scrollToTop,
  } = useUtilities()
  const [formComplete, setFormComplete] = useState(false)
  const [replacementContent, setReplacementContent] =
    useState<React.ReactElement>()

  const prevState = useRef(state)

  // Process fields
  const FIELD_CONFIG = RAW_FIELD_CONFIG.filter((item) => {
    if (apiName === 'voter-registration' && mailinBallotDeadlinePassed) {
      return (
        item.name !== 'mailinBallotInterest' &&
        item.conditionalOn !== 'mailinBallotInterest'
      )
    }
    return true
  })

  useEffect(() => {
    const prevValues = prevState.current
    const handleStateChange = (key: string) => {
      if (stateChangeHandler) {
        stateChangeHandler(key, prevValues[key], state[key], setState, state)
      }
      const field = FIELD_CONFIG.find((item) => item.name === key)
      if (field) {
        validateField(field)
      }
      if (FORM_KEYS_TO_ADD_TO_GLOBAL_STATE.includes(key)) {
        if (globalStore && !submitting) {
          const newPayload = {
            ...toJS(globalStore.state),
            sharedUserInputValues: {
              ...(toJS(globalStore?.state?.sharedUserInputValues) ?? {}),
              [key]: state[key],
            },
          }
          globalStore.setState(newPayload)
        }
      }
    }
    const keysActedOn: string[] = []
    // Check for changes and log them
    Object.keys(state).forEach((key) => {
      if (state[key] !== prevValues[key]) {
        handleStateChange(key)
        keysActedOn.push(key)
      }
    })
    Object.keys(prevValues).forEach((key) => {
      if (!state[key] && !keysActedOn.includes(key)) {
        handleStateChange(key)
      }
    })
    prevState.current = state
  }, [state])

  useEffect(() => {
    setState((prevState) => ({ ...prevState, lang: i18n.language }))
  }, [i18n.language])

  useEffect(() => {
    if (pollingOnSubmit && (!state.pollingId || formComplete)) {
      setState((prevState) => ({
        ...prevState,
        pollingId: randomString(8),
      }))
    }
  }, [pollingOnSubmit, formComplete])

  useEffect(() => {
    if (inReview) {
      scrollToTop(2000)
    }
  }, [inReview])
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const initialState = {
      queryParams: Object.fromEntries(searchParams),
      ...(ONE_STOP_FORMS_TO_SEED.includes(apiName) &&
      (!globalStore?.state?.successfullySubmittedForms ||
        !globalStore?.state.successfullySubmittedForms.includes(apiName))
        ? (toJS(globalStore?.state?.sharedUserInputValues) as KeyAnyValue)
        : {}),
    }
    if (searchParams.get('resetFormState')) {
      const newEmptyState: { [key: string]: any } = {}
      Object.keys(state).forEach((key) => {
        newEmptyState[key] = undefined
      })
      setState({
        ...newEmptyState,
        step: FIRST_STEP,
        lang: i18n.language,
        pollingId: pollingOnSubmit ? randomString(8) : undefined,
        ...initialState,
      })
    } else {
      setState((prevState) => ({
        ...prevState,
        ...initialState,
      }))
    }
  }, [])

  useEffect(() => {
    if (error) {
      alert(error)
    }
  }, [error])

  useEffect(() => {
    if (afterDateAlert) {
      const urlParams = new URLSearchParams(window.location.search)
      const overrideDateParam = urlParams.get('overrideCurrentDate')
      const currentDate = overrideDateParam
        ? new Date(new Date(overrideDateParam).setHours(0, 0, 0, 0))
        : new Date(new Date().setHours(0, 0, 0, 0))

      setShowAfterDateAlert(currentDate.getTime() >= afterDateAlert.getTime())
    }
  }, [afterDateAlert])

  // useEffect(() => {
  //   setState((prevState) => ({
  //     ...prevState,
  //     step: 2,
  //   }))
  // }, [])

  const setStepTo = (step: number) => {
    setInReview(false)
    setFieldErrors([])
    setState((prevState) => ({
      ...prevState,
      step,
    }))
  }

  const { step } = state

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  useEffect(() => {
    if (formRef.current) {
      formRef?.current?.querySelector('input')?.focus()
    }
    if (step === undefined) {
      setState((prevState) => ({
        ...prevState,
        step: FIRST_STEP,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    if (recoveredFormState) {
      if (recoveredFormState?.step === finalStep) {
        setFormComplete(true)
      }
      setState((prevState) => ({
        ...prevState,
        ...recoveredFormState,
        captchaValidated: false,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveredFormState])

  const incrementStep = (direction: 'forward' | 'back' = 'forward') => {
    if (!inReview) {
      setState((prevState) => ({
        ...prevState,
        step: step + (direction === 'forward' ? 1 : -1),
      }))
    } else {
      setInReview(false)
    }
  }

  const getFieldDataForStep = useCallback(
    (specificStep?: number) => {
      const stepForFxn = specificStep ?? step
      return FIELD_CONFIG.filter((field) => {
        return field.step === stepForFxn
      })
        .filter((field) => {
          if (field.conditionalOn && !state[field.conditionalOn]) {
            return false
          }
          if (field.conditionalOff && state[field.conditionalOff]) {
            return false
          }
          if (field.conditionalOn && state[field.conditionalOn]) {
            if (
              field.conditionalOnSelectOption &&
              field.required &&
              field.conditionalOnSelectOption !== state[field.conditionalOn]
            )
              return false
          }
          return true
        })
        .filter((field) => {
          if (
            field.conditionalOn &&
            field.conditionalOnSelectOption &&
            field.conditionalOnSelectOption !== state[field.conditionalOn]
          ) {
            return false
          }
          return true
        })
    },
    [state, FIELD_CONFIG, step],
  )

  const finalStep = useMemo(() => {
    const steps = Array.from(new Set(FIELD_CONFIG.map((field) => field.step)))
    steps.sort((a, b) => b - a)
    let index = 0
    while (index < steps.length) {
      const currentStep = steps[index]
      const fields = getFieldDataForStep(currentStep)
      if (fields.length > 0) {
        return currentStep
      }
      index++
    }
    return steps[0]
  }, [FIELD_CONFIG, getFieldDataForStep])

  const isFinalStep = useMemo(() => step === finalStep, [step, finalStep])

  const submit = async () => {
    const valid = await validate()
    if (!valid) {
      return
    }
    // Steps can be skipped via the single-page headers, so check them all
    if (validateStep) {
      for (let s = FIRST_STEP; s <= finalStep; s++) {
        if (!validateStep(s, state)) {
          setStepTo(s)
          return
        }
      }
    }

    if (reviewSubmission && !inReview) {
      setInReview(true)
      return
    }
    setSubmitting(true)
    const response = await submitForm(state)
    if (response) {
      setFormComplete(true)
      setInReview(false)
    }
    let success = true
    if (validateEventResponse && response) {
      success = validateEventResponse(
        step,
        response,
        setReplacementContent,
        setState,
        'submit',
      )
    }
    const submissions = JSON.parse(localStorage.getItem('submissions') ?? '{}')
    const newSubmissions = JSON.stringify({
      ...submissions,
      [Date.now().toString()]: {
        apiResponse: response,
        formState: state,
      },
    })
    localStorage.setItem('submissions', newSubmissions)

    if (success && response) {
      const newEmptyState: { [key: string]: any } = {}
      Object.keys(state).forEach((key) => {
        newEmptyState[key] = undefined
      })
      setState({ ...newEmptyState })
      if (globalStore) {
        globalStore.setState({
          ...globalStore?.state,
          successfullySubmittedForms: [
            ...(globalStore?.state.successfullySubmittedForms ?? []),
            apiName,
          ],
        })
      }
    }
    setTimeout(() => setSubmitting(false), 500)
  }

  const handleFieldBlur = (e: BlurEvent, field: Field) => {
    if (e.relatedTarget?.tagName !== 'BUTTON') {
      validateField(field)
    }
  }

  const getTextField = (field: Field) => {
    const { name, validate: validateTextField, required, maxLength } = field
    const errorList = getErrorsByField(field)
    return (
      <TextInput
        key={name}
        label={t(`fields.${name}.label`)}
        required={required}
        name={name}
        errorList={errorList}
        onBlur={(e: BlurEvent) => {
          handleFieldBlur(e, field)
        }}
        maxLength={maxLength}
        postalCode={field.validate === 'zip'}
        postalCodeInternational={
          field.postalCodeInternationalControllerField
            ? state[field.postalCodeInternationalControllerField]
            : undefined
        }
        value={state[name]}
        onChange={(value: string) => {
          let shouldChangeState = true
          if (validateTextField) {
            if (validateTextField === 'ssnLastFour') {
              if (value.length > 4 || /[^0-9]/.test(value)) {
                shouldChangeState = false
              }
            }
            if (validateTextField === 'pennDotId') {
              if (value.length > 8 || /[^0-9]/.test(value)) {
                shouldChangeState = false
              }
            }
          }
          if (shouldChangeState) {
            setState((prevState) => ({
              ...prevState,
              [field.name]: value,
            }))
          }
        }}
      />
    )
  }

  const getSelectInput = (field: Field) => {
    const {
      name,
      selectOptions,
      selectLabelsFromOptions,
      required,
      initialValue,
      disabled,
    } = field
    const optionNames = selectOptions
      ? typeof field.selectOptions === 'function'
        ? (selectOptions as any)(state)
        : selectOptions
      : []
    const emptyOptionLabel = t(`selectLabel`) ?? '- Select -'
    const errorList = getErrorsByField(field)
    if (
      field.overrideSelectWithTextControllerField &&
      state[field.overrideSelectWithTextControllerField]
    ) {
      return getTextField(field)
    }
    return (
      <SelectInput
        key={name}
        label={t(`fields.${name}.label`)}
        required={required}
        name={name}
        errorList={errorList}
        onBlur={(e: BlurEvent) => {
          handleFieldBlur(e, field)
        }}
        value={state[name] ?? initialValue ?? undefined}
        disabled={disabled}
        onChange={(event) => {
          const newValue = event.currentTarget?.value ?? ''
          setState((prevState) => ({
            ...prevState,
            [name]: newValue,
          }))
        }}
        options={optionNames.map((optionName: string) => {
          return {
            label: selectLabelsFromOptions
              ? optionName
              : t(`fields.${name}.options.${optionName}`),
            value: optionName,
          }
        })}
        emptyOptionLabel={emptyOptionLabel}
      />
    )
  }

  const getPhoneField = (field: Field) => {
    const { name, required } = field
    return (
      <PhoneInput
        key={name}
        label={t(`fields.${name}.label`)}
        required={required ?? false}
        name={name}
        errorList={getErrorsByField(field)}
        value={state[name]}
        onChange={(newValue) =>
          setState((prevState) => ({
            ...prevState,
            [name]: newValue,
          }))
        }
        onBlur={(e: BlurEvent) => {
          handleFieldBlur(e, field)
        }}
      />
    )
  }

  const getCheckboxField = (fieldItem: Field) => {
    return (
      <CheckboxInput
        key={fieldItem.name}
        label={t(`fields.${fieldItem.name}.label`)}
        name={fieldItem.name}
        required={fieldItem.required}
        checked={state[fieldItem.name] ?? false}
        errorList={getErrorsByField(fieldItem)}
        onBlur={(e: BlurEvent) => {
          handleFieldBlur(e, fieldItem)
        }}
        boldedCheckboxLabel={fieldItem.boldedCheckboxLabel}
        radio={fieldItem.type === 'radio'}
        onChange={(value) => {
          if (!value) {
            FIELD_CONFIG.filter(
              (field) => field.conditionalOn === fieldItem.name,
            ).forEach((field) => {
              setState((prevState) => {
                let newState: { [key: string]: any } = {}
                Object.keys(prevState).forEach((key) => {
                  if (key !== field.name) {
                    newState[key] = prevState[key]
                  } else {
                    newState[key] = undefined
                  }
                })
                return newState
              })
            })
          }
          if (fieldItem.radioGroup) {
            FIELD_CONFIG.filter(
              (item) =>
                item.radioGroup === fieldItem.radioGroup &&
                item.name !== fieldItem.name,
            ).forEach((memberField) => {
              setState((prevState) => ({
                ...prevState,
                [memberField.name]: undefined,
              }))
            })
          }
          setState((prevState) => ({
            ...prevState,
            [fieldItem.name]: value === true ? true : undefined,
          }))
        }}
      />
    )
  }

  const getDateField = (field: Field) => {
    const {
      name,
      required,
      fixedTodaysDate,
      maxDefaultDate: startDateOnClick,
      dateOnlyYear,
    } = field
    const today = new Date()
    const initialDate = state[name] ?? undefined
    if (!state[name] && fixedTodaysDate) {
      setState((prevState) => ({
        ...prevState,
        [name]: today,
      }))
    }
    return (
      <DateInput
        key={name}
        name={name}
        initialValue={initialDate}
        label={t(`fields.${name}.label`)}
        onChange={(value) => {
          setState((prevState) => ({
            ...prevState,
            [name]: value,
          }))
        }}
        startDateOnClick={startDateOnClick ?? undefined}
        maxDate={startDateOnClick ?? undefined}
        fixedTodaysDate={fixedTodaysDate}
        required={required}
        onlyYear={dateOnlyYear}
        errorList={getErrorsByField(field)}
        onBlur={(e: BlurEvent) => {
          handleFieldBlur(e, field)
        }}
      />
    )
  }

  const getSignatureField = (field: Field) => {
    const errorList = getErrorsByField(field)
    return (
      <SignatureInput
        onEnd={(imageString) => {
          setState((prevState) => ({
            ...prevState,
            [field.name]: imageString ?? true,
          }))
        }}
        onClear={() => {
          setState((prevState) => ({
            ...prevState,
            [field.name]: false,
          }))
        }}
        name={field.name}
        errorList={errorList}
        required={field.required}
        label={t(`fields.${field.name}.label`)}
      />
    )
  }

  const getHtmlContentField = (name: string, htmlFontSizeSmall: boolean) => {
    const fontStyle = htmlFontSizeSmall
      ? { fontSize: 12, lineHeight: '14px' }
      : {}
    return (
      <div
        style={fontStyle}
        dangerouslySetInnerHTML={{ __html: t(`fields.${name}.label`) }}
      ></div>
    )
  }

  const getAccordionField = (field: Field) => {
    return (
      <Accordion
        title={t(`fields.${field.name}.title`)}
        content={t(`fields.${field.name}.content`)}
        noHeaderBottomMargin={field.accordionNoHeaderBottomMargin}
      />
    )
  }

  const getErrorsByField = useCallback(
    (field: Field) => {
      return fieldErrors.filter((item) => item.name === field.name)
    },
    [fieldErrors],
  )

  const renderFieldComponent = (field: Field) => {
    switch (field.type) {
      case 'select':
        return getSelectInput(field)
      case 'html':
        return getHtmlContentField(field.name, field.htmlFontSizeSmall ?? false)
      case 'text':
        return getTextField(field)
      case 'phone':
        return getPhoneField(field)
      case 'date':
        return getDateField(field)
      case 'checkbox':
      case 'radio':
        return getCheckboxField(field)
      case 'signature':
        return getSignatureField(field)
      case 'accordion':
        return getAccordionField(field)
      default:
        return null
    }
  }

  const allStepNumbers = Array.from(
    { length: finalStep - FIRST_STEP + 1 },
    (_, i) => FIRST_STEP + i,
  )

  const renderAllFields = () => {
    const components: any = []
    for (const i of allStepNumbers) {
      components.push(
        <>
          {header(i, 'top')}
          {renderFields(i)}
        </>,
      )
    }
    return components
  }

  const renderFields = (specificStep = 0) => {
    const rawFields = getFieldDataForStep(specificStep ?? step)
    let fieldGroup = ''
    let fieldGroupChildren: (JSX.Element | null)[] = []
    const components: (JSX.Element | null)[] = []
    const addFieldGroup = (newFieldGroup = '') => {
      components.push(
        <FieldSet
          children={fieldGroupChildren}
          label={t(`fieldGroups.${fieldGroup}`)}
          required={requiredFieldGroups?.includes(fieldGroup)}
        />,
      )
      fieldGroupChildren = []
      fieldGroup = newFieldGroup
    }
    const addFieldToFieldGroup = (field: Field) => {
      fieldGroupChildren.push(renderFieldComponent(field))
    }
    rawFields.forEach((field, index) => {
      if (field.fieldGroup) {
        if (!fieldGroup) {
          fieldGroup = field.fieldGroup
          addFieldToFieldGroup(field)
        } else if (fieldGroup !== field.fieldGroup) {
          addFieldGroup(field.fieldGroup)
          addFieldToFieldGroup(field)
          if (!rawFields[index + 1]) {
            addFieldGroup()
          }
        } else if (!rawFields[index + 1]) {
          addFieldToFieldGroup(field)
          addFieldGroup()
        } else {
          addFieldToFieldGroup(field)
        }
      } else if (fieldGroup) {
        addFieldGroup()
      } else {
        components.push(renderFieldComponent(field))
      }
    })

    return (
      <>
        {components}
        {!state.captchaValidated &&
        (recoveredFormState || mailInBallotConfig.captchaStep === step) ? (
          <ReCAPTCHA
            hl={i18n.language}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={(value) => {
              setTimeout(() => {
                setState((prevState) => ({
                  ...prevState,
                  captchaValidated: true,
                }))
              }, 500)
            }}
          />
        ) : null}
      </>
    )
  }

  const validateField = async (
    field: Field,
    alterState = true,
  ): Promise<FieldError[]> => {
    const errorsInField = []
    if (field.required && !state[field.name]) {
      errorsInField.push({
        name: field.name,
        errorText: field.customRequiredErrorMessage
          ? t(`fields.${field.name}.customRequiredErrorMessage`)
          : globalT(`fieldErrors.required`),
        errorType: 'required',
      })
    }
    if (!field.required && !state[field.name]) {
      if (alterState) {
        const newFieldErrors = fieldErrors.filter(
          (error) => error.name !== field.name,
        )
        setFieldErrors([...newFieldErrors, ...errorsInField])
      }
      return []
    }
    if (field.validate === 'email' && !validateEmail(state[field.name])) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidEmail`),
        errorType: field.validate,
      })
    }
    if (field.validate === 'phone' && !validatePhoneNumber(state[field.name])) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidPhone`),
        errorType: field.validate,
      })
    }
    if (field.validate === 'signature') {
      const validSignature = await validateSignature(state[field.name])
      if (!validSignature) {
        errorsInField.push({
          name: field.name,
          errorText: globalT(`fieldErrors.invalidSignature`),
          errorType: field.validate,
        })
      }
    }
    if (
      field.validate === 'date' &&
      !validateDate(
        state[field.name],
        field.dateOnlyYear,
        field.maxDefaultDate,
        field.minDate,
      )
    ) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidDate`),
        errorType: field.validate,
      })
      if (field.name === 'birthDate' && field.minDate && field.maxDefaultDate) {
        const dateValue = new Date(state[field.name])
        if (dateValue.getFullYear() < field.minDate.getFullYear()) {
          errorsInField.push({
            name: field.name,
            errorText: `Value must be ${formatDateToMMDDYYYY(
              field.minDate,
            )} or later.`,
            errorType: field.validate,
          })
        }
        if (dateValue.getTime() > field.maxDefaultDate.getTime()) {
          errorsInField.push({
            name: field.name,
            errorText: `Value must be ${formatDateToMMDDYYYY(
              field.maxDefaultDate,
            )} or earlier. You must be at least 18 years old on election day`,
            errorType: field.validate,
          })
        }
      }
    }
    if (
      field.validate === 'zip' &&
      !validateZip(
        state[field.name],
        field.postalCodeInternationalControllerField
          ? state[field.postalCodeInternationalControllerField]
          : undefined,
      )
    ) {
      errorsInField.push({
        name: field.name,
        errorText:
          field.postalCodeInternationalControllerField &&
          state[field.postalCodeInternationalControllerField]
            ? 'Postal Code should have an alphanumeric set of characters that is less than or equal to 10 characters in length.'
            : field.maxLength === 5
              ? globalT(`fieldErrors.invalidZip5`)
              : globalT(`fieldErrors.invalidZip`),
        errorType: field.validate,
      })
    }
    if (
      field.validate === 'pennDotId' &&
      !validatePennDotId(state[field.name])
    ) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidPennDotId`),
        errorType: field.validate,
      })
    }
    if (
      field.validate === 'ssnLastFour' &&
      !validateSsnLastFour(state[field.name])
    ) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidSsnLastFour`),
        errorType: field.validate,
      })
    }
    if (
      field.validate === 'idCard' &&
      !validateSsnLastFour(state[field.name]) &&
      !validatePennDotId(state[field.name])
    ) {
      errorsInField.push({
        name: field.name,
        errorText: globalT(`fieldErrors.invalidIdCard`),
        errorType: field.validate,
      })
    }

    if (
      field.maxLength !== undefined &&
      state[field.name]?.length > field.maxLength
    ) {
      errorsInField.push({
        name: field.name,
        errorText: `This field allows a maximum of ${field.maxLength}`,
        errorType: undefined,
      })
    }
    if (errorsInField.length > 0 && field.customErrorMessage) {
      errorsInField.push({
        name: field.name,
        errorText: t(`fields.${field.name}.customErrorMessage`),
        errorType: field.validate,
      })
    }
    if (alterState) {
      const newFieldErrors = fieldErrors.filter(
        (error) => error.name !== field.name,
      )
      setFieldErrors([...newFieldErrors, ...errorsInField] as any)
    }
    return errorsInField as any
  }

  const getFieldDataForAllSteps = () => {
    let fields: Field[] = []
    for (const i of allStepNumbers) {
      fields = [...fields, ...getFieldDataForStep(i)]
    }
    return fields
  }

  const validate = async (shouldAlert: boolean = true) => {
    const fields = inReview ? getFieldDataForAllSteps() : getFieldDataForStep()
    const fieldNames = fields.map((field) => field.name)
    const newFieldErrors: FieldError[] = []
    for (const field of fields) {
      const errors = await validateField(field, false)
      errors.forEach((error) => {
        newFieldErrors.push(error)
      })
    }

    if (
      (requireEitherEmailOrPhone && fieldNames.includes('email')) ||
      fieldNames.includes('phone')
    ) {
      if (!state.phone && !state.email) {
        const pushEmailPhoneError = (name: string) => {
          newFieldErrors.push({
            name: name,
            errorText: globalT(`fieldErrors.emailOrPhoneRequired`),
            errorType: 'required',
          })
        }
        pushEmailPhoneError('phone')
        pushEmailPhoneError('email')
      }
    }

    setFieldErrors([...newFieldErrors])
    if (newFieldErrors.length && shouldAlert) {
      setTimeout(() => alert(`Please review the errors in the form.`), 0)
      return false
    }
    if (
      shouldAlert &&
      !state.captchaValidated &&
      (recoveredFormState || mailInBallotConfig.captchaStep === step)
    ) {
      alert(`You must complete the captcha challenge.`)
      return false
    }
    return true
  }

  const continueToNextStep = async () => {
    const valid = await validate()
    if (valid && (!validateStep || validateStep(step, state))) {
      if (!validateEventResponse || validateEventResponseType === 'submit') {
        triggerFormEvent(state)
        incrementStep()
      } else {
        const response = await triggerFormEvent(state)

        if (response) {
          if (validateEventResponse) {
            const valid = validateEventResponse(
              step,
              response,
              setReplacementContent,
              setState,
              'event',
            )
            if (valid) {
              incrementStep()
            }
          } else {
            incrementStep()
          }
        }
      }
    }
  }

  const Header: React.FC<{
    headerStep: number
    status?: 'complete' | 'incomplete' | 'progress'
  }> = ({ headerStep, status = 'complete' }) => {
    const text = t(`stepHeaders.${headerStep}`, { defaultValue: '' })
    const fields = getFieldDataForStep(headerStep)
    if (fields.length > 0 && text) {
      return (
        <h2 className={`form__step-header form__step-header--${status}`}>
          <div
            className={`form__step-header__number form__step-header__number--${status}`}
          >
            {status === 'complete' ? (
              <BsCheck color="white" />
            ) : (
              headerStep.toString()
            )}
          </div>
          <div className="form__step-header__text">{text}</div>
        </h2>
      )
    }
    return null
  }

  const Alert: React.FC = () => {
    const content = formContent?.formOverrides[apiName]

    const alertOverride = content?.alert
    if (alertOverride && !alertOverride?.enabled) {
      return null
    }

    if (showAfterDateAlert || alertOverride?.enabled) {
      return (
        <div className="form-alert">
          <div className="form-alert__inner">
            <div className="form-alert__icon">
              <BsExclamationTriangleFill size={40} />
            </div>

            <p
              className="form-alert__text"
              dangerouslySetInnerHTML={{
                __html:
                  alertOverride?.content && alertOverride?.enabled
                    ? alertOverride.content[i18n.language]
                    : t('afterDateAlert'),
              }}
            ></p>
          </div>
        </div>
      )
    } else {
      return null
    }
  }

  const ActionButton: React.FC = () => {
    return (
      <Button
        label={bottomButtonText}
        onClick={() => (isFinalStep ? submit() : continueToNextStep())}
        loading={loading}
      />
    )
  }

  const doesStepHaveInputs = useCallback(
    (testStep: number) => {
      return (
        FIELD_CONFIG.filter((item) => item.step === testStep).filter(
          (item) => state[item.name] !== undefined,
        ).length > 0
      )
    },
    [FIELD_CONFIG, state],
  )
  const requiredFieldsInStep = useCallback(
    (testStep: number) => {
      return getFieldDataForStep(testStep).filter((item) => item.required)
    },
    [getFieldDataForStep],
  )
  const areAllRequiredFieldsComplete = useCallback(
    (testStep: number) => {
      const requiredFieldsNotComplete = getFieldDataForStep(testStep)
        .filter((item) => item.required)
        .filter((item) => !state[item.name])
      return requiredFieldsNotComplete.length === 0
    },
    [getFieldDataForStep, state],
  )

  const header = (headerStep: number, position: 'top' | 'bottom' = 'top') => {
    let status: 'complete' | 'incomplete' | 'progress' =
      requiredFieldsInStep(headerStep).length > 0 &&
      areAllRequiredFieldsComplete(headerStep)
        ? 'complete'
        : doesStepHaveInputs(headerStep)
          ? 'progress'
          : 'incomplete'
    if (
      requiredFieldsInStep(headerStep).length === 0 &&
      (doesStepHaveInputs(headerStep) ||
        doesStepHaveInputs(headerStep + 1) ||
        step > headerStep)
    ) {
      status = 'complete'
    }
    return (
      <button
        className={`single-page__header-wrapper single-page__header-wrapper--${status}`}
        key={`header__${headerStep}`}
        onClick={async (e) => {
          e.preventDefault()
          e.stopPropagation()
          if (inReview) {
            return
          }
          if (['complete', 'progress'].includes(status)) {
            if (position === 'bottom') {
              if (
                (await validate()) &&
                (!validateStep || validateStep(step, state))
              ) {
                setStepTo(headerStep)
              }
            } else {
              setStepTo(headerStep)
            }
          }
        }}
      >
        <Header key={headerStep} headerStep={headerStep} status={status} />
      </button>
    )
  }

  const singlePageHeaders = (position: 'top' | 'bottom' = 'top') => {
    //  Render headers
    const topSteps = Array.from({ length: step }, (_, i) => i + 1)
    const bottomSteps = Array.from(
      { length: finalStep - step },
      (_, i) => i + step + 1,
    )

    return (position === 'top' ? topSteps : bottomSteps).map((headerStep) => {
      return header(headerStep, position)
    })
  }
  const bottomButtonText = useMemo(() => {
    if (inReview) {
      return globalT('submit')
    } else if (reviewSubmission && isFinalStep) {
      return globalT('review')
    }
    return globalT(`${isFinalStep ? 'submit' : 'continue'}`)
  }, [isFinalStep, inReview, globalT, reviewSubmission])

  const formRef = useRef(null as HTMLElement | null)

  const Review = () => {
    return (
      <ReviewSubmission
        formState={state}
        i18nName={i18nName}
        fieldConfig={FIELD_CONFIG}
        onClickEdit={(stepOfInput: number, inputName: string) => {
          setStepTo(stepOfInput)

          setTimeout(() => {
            const input = formRef?.current?.querySelector(
              `[name="${inputName}"]`,
            ) as any
            if (input) {
              input.focus()
              input.scrollIntoView({ behavior: 'smooth' })
            }
          }, 250)
        }}
      />
    )
  }

  if (formContent?.formOverrides[apiName]?.scheduledDowntime) {
    const downTime = formContent?.formOverrides[apiName]?.scheduledDowntime
    const startDate = new Date(downTime?.start as any)
    const endDate = new Date(downTime?.end as any)
    const now = new Date()
    if (
      downTime?.start &&
      downTime?.end &&
      now.getTime() < endDate.getTime() &&
      now.getTime() > startDate.getTime()
    ) {
      return (
        <FormWrapper
          id={`${apiName}-form`}
          title={t(`title`)}
          formRef={formRef}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: downTime.content[i18n.language],
            }}
          />
        </FormWrapper>
      )
    }
  }

  if (apiName === 'mail-in-ballot' && mailinBallotDeadlinePassed) {
    const dates = formContent?.dates
    return (
      <FormWrapper id={`${apiName}-form`} title={t(`title`)} formRef={formRef}>
        <div
          dangerouslySetInnerHTML={{
            __html: t(`deadlinePassedCompleteScreen`, {
              electionDay: dates?.electionDay,
            }),
          }}
        />
      </FormWrapper>
    )
  }

  if (replacementContent) {
    return (
      <FormWrapper id={`${apiName}-form`} title={t(`title`)} formRef={formRef}>
        {replacementContent}
      </FormWrapper>
    )
  }

  if (singlePage) {
    return (
      <FormWrapper
        id={`${apiName}-form`}
        title={t(`title`)}
        formRef={formRef}
        headerContent={headerContent}
      >
        {apiName !== 'mail-in-ballot' && <Alert />}
        {AboveFieldsContent && (
          <>
            <AboveFieldsContent
              step={step}
              isReviewing={inReview}
              formComplete={formComplete}
            />
            {apiName === 'mail-in-ballot' && <Alert />}
          </>
        )}

        {!formComplete ? (
          <>
            {!inReview ? (
              <>
                {singlePageHeaders()}
                {renderFields(step)}
                <ActionButton />
                {singlePageHeaders('bottom')}
              </>
            ) : (
              <>
                <div
                  className="form__back-button"
                  onClick={() => setInReview(false)}
                >
                  {'<< ' + t(`back`)}
                </div>
                <h3 className="review-submission__header">
                  {globalT('reviewHeader')}
                </h3>
                {renderAllFields()}
                <ActionButton />
              </>
            )}
          </>
        ) : SuccessScreen ? (
          <SuccessScreen />
        ) : (
          <>
            <h4>{t(`name`)}</h4>
            <p>
              <a href="https://vote.pa">{t(`accessResources`)}</a>
            </p>
          </>
        )}
        {BelowFieldsContent && (
          <BelowFieldsContent
            step={step}
            isReviewing={inReview}
            formComplete={formComplete}
          />
        )}
      </FormWrapper>
    )
  }

  return (
    <>
      <FormWrapper
        id={`${apiName}-form`}
        title={t(`title`)}
        formRef={formRef}
        headerContent={headerContent}
      >
        {AboveFieldsContent && (
          <AboveFieldsContent
            step={step}
            isReviewing={inReview}
            formComplete={formComplete}
          />
        )}
        {!formComplete ? (
          <>
            {step > FIRST_STEP && (
              <div
                className="form__back-button"
                onClick={() => incrementStep('back')}
              >
                {'<< ' + t(`back`)}
              </div>
            )}
            {!inReview && <Header headerStep={step} />}
            <Alert />
            {!inReview ? renderFields(step) : <Review />}
            <ProgressTracker
              currentStep={step}
              steps={finalStep}
              setStepTo={setStepTo}
              i18nName={i18nName}
            />
            <ActionButton />
          </>
        ) : SuccessScreen ? (
          <SuccessScreen />
        ) : (
          <>
            <h4>{t(`name`)}</h4>
            <p>
              <a href="https://vote.pa">{t(`accessResources`)}</a>
            </p>
          </>
        )}
        {BelowFieldsContent && (
          <BelowFieldsContent
            step={step}
            isReviewing={inReview}
            formComplete={formComplete}
          />
        )}
      </FormWrapper>
      {footerContent ? (
        <footer className="form__footer">
          {React.createElement(footerContent)}
        </footer>
      ) : null}
    </>
  )
}
